if (!customElements.get('product-inventory')) {
  class ProductInventory extends HTMLElement {
    constructor() {
      super();
      if (this.hidden) {
        this.initLazySection();
      } else {
        window.initLazyScript(this, this.initLazySection.bind(this));
      }
    }

    initLazySection() {
      this.section = this.closest('.js-product');
      this.inventoryNotice = this.querySelector('.js-inventory-notice');
      this.urgencyMessage = this.querySelector('.js-inventory-urgency');
      this.indicatorBar = this.querySelector('.js-inventory-indicator');

      // Bind to on:variant:change event for this product
      this.section.addEventListener('on:variant:change', this.handleVariantChange.bind(this));

      // Init
      this.updateInventory(
        parseInt(this.dataset.inventoryQuantity, 10),
        this.dataset.variantAvailable === 'true',
        this.dataset.inventoryPolicy
      );
    }

    /**
     * Gets the inventory data for all product variants
     * @returns {?object}
     */
    getVariantInventory() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Handles a 'change' event on the variant picker element
     * @param {object} evt - Event object
     */
    handleVariantChange(evt) {
      if (evt.detail.variant) {
        const inventory = this.getVariantInventory();
        this.updateInventory(
          inventory.inventory_quantity,
          inventory.available,
          inventory.inventory_policy
        );
      } else {
        this.updateInventory(0, false);
        this.hidden = true;
      }
    }

    /**
     *
     * Updates the inventory notice
     * @param {number} count - the inventory quantity available
     * @param {boolean} available - whether current variant is available
     * @param {string} inventoryPolicy - whether product continues selling when out of stock
     */
    updateInventory(count, available, inventoryPolicy) {
      if (count === 0 && !available && this.hidden) {
        return;
      }

      let inventoryLevel;

      if (count <= 0) {
        if (inventoryPolicy === 'continue') {
          inventoryLevel = 'backordered';
        } else if (available) {
          inventoryLevel = 'in_stock';
        } else {
          inventoryLevel = 'none';
        }
      } else if (count <= parseInt(this.dataset.thresholdVeryLow, 10)) {
        inventoryLevel = 'very_low';
      } else if (count <= parseInt(this.dataset.thresholdLow, 10)) {
        inventoryLevel = 'low';
      } else {
        inventoryLevel = 'normal';
      }

      if (((this.dataset.showNotice === 'always'
        || (this.dataset.showNotice === 'low' && inventoryLevel.includes('low')))
        && !(inventoryLevel === 'backordered' && this.dataset.showNoStockBackordered === 'false'))) {
        this.hidden = false;

        // Set the inventory level data attribute
        this.setAttribute('data-inventory-level', inventoryLevel);

        // Determine whether to show the count or not
        if (inventoryLevel === 'backordered') {
          this.inventoryNotice.innerText = theme.strings.backordered;
        } else if (inventoryLevel !== 'in_stock' && (this.dataset.showCount === 'always' || (this.dataset.showCount === 'low' && inventoryLevel.includes('low')))) {
          this.inventoryNotice.innerText = theme.strings.onlyXLeft.replace('[quantity]', count);
        } else if (inventoryLevel === 'very_low') {
          this.inventoryNotice.innerText = theme.strings.veryLowStock;
        } else if (inventoryLevel === 'low') {
          this.inventoryNotice.innerText = theme.strings.lowStock;
        } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
          this.inventoryNotice.innerText = theme.strings.inStock;
        } else if (inventoryLevel === 'none') {
          this.inventoryNotice.innerText = theme.strings.noStock;
        }

        // Update urgency message if needed
        if (this.urgencyMessage) {
          if (inventoryLevel === 'very_low') {
            this.urgencyMessage.innerHTML = this.dataset.textVeryLow;
          } else if (inventoryLevel === 'low') {
            this.urgencyMessage.innerHTML = this.dataset.textLow;
          } else if (inventoryLevel === 'backordered') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStockBackordered;
          } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
            this.urgencyMessage.innerHTML = this.dataset.textNormal;
          } else if (inventoryLevel === 'none') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStock;
          }
        }

        // Update the bar indicator
        if (this.indicatorBar) {
          this.indicatorBar.hidden = false;

          let newWidth;
          if ((count >= this.dataset.scale || inventoryLevel === 'in_stock') && inventoryLevel !== 'backordered') {
            newWidth = 100;
          } else {
            newWidth = ((100 / parseInt(this.dataset.scale, 10)) * count).toFixed(1);
          }
          this.indicatorBar.querySelector('span').style.width = `${newWidth}%`;
        }

        this.rgFurnizor(inventoryLevel);
      } else {
        this.hidden = true;
      }
    }

    /**
     * RG (03.11): produsele aduse de la furnizor (data-rg-furn = zilele furnizorului; data-rg-regula = cand se adauga o
     * zi: "central" dupa 18:00 L–J, sambata dupa 13:00 si duminica, "conex" dupa 17:30 si in weekend) – „În stoc la
     * furnizor · ajunge la noi în N zile”, culoarea proprie, bara plina. Conex cu stoc in Suceava / Botosani / Gura
     * Humorului (data-rg-local = ora-limita, 14:50 / 11:00), L–V pana atunci: vine in aceeasi zi, ramane „În stoc”.
     * @param {string} inventoryLevel - nivelul calculat de updateInventory
     */
    rgFurnizor(inventoryLevel) {
      let z = parseInt(this.dataset.rgFurn || '0', 10) || 0;
      if (!z || inventoryLevel === 'none' || inventoryLevel === 'backordered') return;
      const p = {};
      new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Bucharest', weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
        .formatToParts(new Date()).forEach((x) => { p[x.type] = x.value; });
      const ora = (parseInt(p.hour, 10) % 24) * 60 + parseInt(p.minute, 10);
      const zi = p.weekday;
      const lucru = zi !== 'Sat' && zi !== 'Sun';
      const rg = this.dataset.rgRegula;
      const tarziu = (rg === 'central' && (zi === 'Sun' || (zi === 'Sat' && ora >= 13 * 60) || (lucru && zi !== 'Fri' && ora >= 18 * 60)))
        || (rg === 'conex' && (!lucru || ora >= 17 * 60 + 30));
      const loc = (this.dataset.rgLocal || '').split(':');
      const lim = parseInt(loc[0], 10) * 60 + (parseInt(loc[1], 10) || 0);
      if (lim > 0 && p.weekday !== 'Sat' && p.weekday !== 'Sun' && ora < lim) return;
      if (tarziu) z += 1;
      this.setAttribute('data-inventory-level', 'rg_furnizor');
      this.inventoryNotice.innerHTML = `În stoc la furnizor<span class="rg-inv-furn"> · ajunge la noi în ${z} ${z === 1 ? 'zi' : 'zile'}</span>`;
      if (this.urgencyMessage) this.urgencyMessage.innerHTML = '';
      if (this.indicatorBar) this.indicatorBar.querySelector('span').style.width = '100%';
    }
  }

  customElements.define('product-inventory', ProductInventory);
}
